import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';

import Card from 'components/Card';
import Spinner from 'components/Spinner';
import CompareBarChart from 'modules/Prices/pages/compare/compareBarChart';
import PricesContainer from 'modules/Prices/components/container';
import ComparePriceFilters from 'modules/Prices/pages/compare/components/Filters';
import PricesCompareHeader from 'modules/Prices/pages/compare/components/Header';
import PricesCompareSelectCompetitor from 'modules/Prices/pages/compare/selectCompetitor';

import { usePricesFilters, usePricesCompare } from 'apiTyped/hooks/prices';
import {
  dataFilter,
  getMarketOptions,
  getNameNetwork
} from 'modules/Prices/helpers';
import {
  getChartData,
  getDataFilters
} from 'modules/Prices/pages/compare/helpers';
import { MarketOption } from 'interface';
import { FilterComparePricesOptions } from 'interface/price';
import { connect } from 'react-redux';

const PricesCompareDiv = styled.div`
  margin-bottom: 40px;

  .chartsContainer {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    grid-gap: 20px;
  }

  > *:not(:last-child) {
    margin-bottom: 20px;
  }

  .selectSetsContainer {
    margin-top: 20px;
    margin-bottom: 20px;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};

    > *:not(:last-child) {
      margin-right: 20px;
    }
  }
`;

const PricesCompare = () => {
  const { isLoadingPricesFilters, filters, networks } = usePricesFilters();

  const [wineId, setWineId] = useState<number | null>(null);
  const [market, setMarket] = useState<MarketOption | null>(null);
  const [network, setNetwork] = useState<string | null>(null);
  const [vintageStart, setVintageStart] = useState<number | null>(null);
  const [vintageEnd, setVintageEnd] = useState<number | null>(null);
  const [competitor, setCompetitor] = useState<string | null>(null);
  const [activeRadioButton, setActiveRadioButton] =
    useState('Aucune comparaison');

  const { isLoadingPricesCompare, data } = usePricesCompare(
    !!wineId,
    wineId === null ? undefined : wineId
  );

  const defaultCurrency = useMemo(
    () => get(data, '[0].displayed_currency', 'EUR'),
    [data]
  );

  const [currency, setCurrency] = useState<string>(defaultCurrency);

  const wineName = wineId
    ? filters.find((item) => item.wine_id === wineId).wine_name
    : null;

  const getCompetitors = () => {
    return wineId
      ? get(
          filters.find((item) => item.wine_id === wineId),
          'rival_wines',
          []
        )
      : [];
  };

  // Set initial wineId currency and network values
  useEffect(() => {
    if (!wineId && filters.length > 0) {
      const defaultWine = get(filters, '[0]', null);
      setWineId(get(defaultWine, 'wine_id', null));
      setNetwork(get(networks, '[0]', 'ON'));
    }
  }, [filters]);

  useEffect(() => {
    setMarket(null);

    if (network === 'WEB') {
      setActiveRadioButton('Aucune comparaison');
      setCompetitor(null);
    }
  }, [network, wineId]);

  useEffect(() => setCurrency(defaultCurrency), [data]);

  const chartData = getChartData({
    data,
    wine: wineName,
    market,
    network,
    activeRadioButton,
    vintageStart,
    vintageEnd,
    competitor,
    currency
  });

  // Possible networks according to data (before applying market filter)
  const possibleNetworksFromData = [
    ...new Set(data.map((item: any) => item.network_code))
  ];

  const options: FilterComparePricesOptions = {
    wines: [
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...new Set(
        filters.map((item) => ({
          value: item.wine_id,
          label: item.wine_name
        }))
      )
    ],
    networks: [...new Set(networks)]
      .filter((network) => getNameNetwork(network))
      .map((network) => ({
        value: network,
        label: getNameNetwork(network),
        disabled: !possibleNetworksFromData.includes(network)
      })),
    markets: getMarketOptions(
      dataFilter(data, getDataFilters({ wine: wineName, network }))
    )
  };

  const exportName = [
    network ? getNameNetwork(network) : null,
    market ? market.label : null,
    wineName
  ]
    .filter((item) => item)
    .join(' - ');

  const competitors = getCompetitors();

  return (
    <PricesContainer>
      <PricesCompareDiv>
        <ComparePriceFilters
          options={options}
          wineId={wineId} // @ts-expect-error test
          market={market}
          network={network}
          onChangeWine={setWineId}
          onChangeNetwork={setNetwork}
          onChangeMarket={(value) => {
            setCurrency(
              value && value.currency ? value.currency : defaultCurrency
            );
            setMarket(value);
          }}
          disabled={isLoadingPricesFilters || isLoadingPricesCompare}
        />
        <div className="chartsContainer">
          <Card id="prices-compare">
            <PricesCompareHeader
              data={data}
              exportName={exportName}
              setVintageStart={setVintageStart}
              setVintageEnd={setVintageEnd}
            />
            <CompareBarChart
              data={chartData}
              activeRadioButton={activeRadioButton}
              currency={currency}
            />
            <PricesCompareSelectCompetitor
              network={network}
              competitor={competitor}
              setActiveRadioButton={setActiveRadioButton}
              competitors={competitors}
              setCompetitor={setCompetitor}
            />
          </Card>
          <Spinner
            loading={isLoadingPricesFilters || isLoadingPricesCompare}
            little
          />
        </div>
      </PricesCompareDiv>
    </PricesContainer>
  );
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, {})(PricesCompare);
